import { useMutation } from '@apollo/client/react';
import { useAtomValue, useSetAtom } from 'jotai';
import { useCallback, useEffect, useState } from 'react';

import {
  DeleteGuestPatientFollowupCommentDocument,
  DeletePatientFollowupCommentDocument,
  GetPatientFollowupCommentsDocument,
} from '~/graphql/generated/graphql';
import {
  eventPageInfoState,
  patientDetailDialogPatientCommentState,
  patientDetailDialogState,
} from '~/state/partials/patient_detail_dialog/atoms';

export const useDeletePatientComment = (
  patientType: 'Patient' | 'DirectVisitor' | 'WebVisitor' | 'LineVisitor' | undefined,
) => {
  const [error, setError] = useState<string | null>(null);
  const patientDetailCommentState = useAtomValue(patientDetailDialogPatientCommentState);
  const patientDetailState = useAtomValue(patientDetailDialogState);

  const setEventInfoState = useSetAtom(eventPageInfoState);
  const [deletePatientComment, { data: deletedPatientCommentData, loading, called }] = useMutation(
    DeletePatientFollowupCommentDocument,
    {
      refetchQueries: [
        {
          query: GetPatientFollowupCommentsDocument,
          variables: {
            id: patientDetailState.patientId,
          },
        },
      ],
    },
  );
  const [
    deleteGuestPatientComment,
    { data: deletedGuestPatientCommentData, loading: guestLoading, called: guestCalled },
  ] = useMutation(DeleteGuestPatientFollowupCommentDocument, {
    refetchQueries: [
      {
        query: GetPatientFollowupCommentsDocument,
        variables: {
          id: patientDetailState.patientId,
        },
      },
    ],
  });

  useEffect(() => {
    if (
      !deletedPatientCommentData?.deletePatientMedicationFollowupComment &&
      !deletedGuestPatientCommentData?.deleteGuestPatientMedicationFollowupComment
    ) {
      return;
    }
    setEventInfoState((_state) => ({ ..._state, isResetEvent: true, page: 1, perPage: 20 }));
  }, [deletedGuestPatientCommentData, deletedPatientCommentData, setEventInfoState]);

  const deleteComment = useCallback(async () => {
    if (patientDetailCommentState.selectedCommentId === null) return;
    if (patientType === undefined) return;

    try {
      if (patientType === 'Patient') {
        await deletePatientComment({
          variables: {
            input: {
              patientMedicationFollowupCommentId: patientDetailCommentState.selectedCommentId,
            },
          },
        });
      } else {
        await deleteGuestPatientComment({
          variables: {
            input: {
              guestPatientMedicationFollowupCommentId: patientDetailCommentState.selectedCommentId,
            },
          },
        });
      }
    } catch (_error) {
      setError(_error?.message || 'エラーが発生しました');
      throw new Error('', { cause: _error });
    }
  }, [
    deleteGuestPatientComment,
    deletePatientComment,
    patientDetailCommentState.selectedCommentId,
    patientType,
  ]);

  return {
    deleteComment,
    error,
    isDeleting: (loading && called) || (guestLoading && guestCalled),
  };
};
